import { Plus, RefreshCw, Search, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { FaEye } from "react-icons/fa";
import { Button } from "../../app/components";
import MainHeader from "../Common/MainHeader/MainHeader";
import { auth } from "../../firebase/config";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { getClientes } from "../../services/firestoreService";
import { deleteClienteByUser, salvarCliente } from "../../services/userService";
import { Cliente, ClienteFormData } from "../../types";
import ConfirmModal from "../Common/ConfirmModal";
import { DataList, DataListColumn } from "../Common/DataList";
import { Pagination } from "../Common/Pagination";
import ClienteForm from "./ClienteForm";
import "./ClientesList.css";

const ClientesList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { modalState, showConfirm, hideConfirm, handleConfirm } =
    useConfirmModal();

  // Aguarda a autenticação do Firebase
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  // Carrega todos os clientes quando userId está disponível
  // Implementa cache em memória para reduzir leituras do Firestore
  const loadClientes = async (forceRefresh: boolean = false) => {
    if (!userId) return;

    // Verifica se já tem dados em cache e não é refresh forçado
    const cacheKey = `clientes_${userId}`;
    const cacheTimeKey = `clientes_${userId}_time`;
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos em ms

    if (!forceRefresh) {
      const cachedData = sessionStorage.getItem(cacheKey);
      const cachedTime = sessionStorage.getItem(cacheTimeKey);

      if (cachedData && cachedTime) {
        const age = Date.now() - parseInt(cachedTime);
        if (age < CACHE_DURATION) {
          // Usando cache - economiza leituras do Firestore
          setClientes(JSON.parse(cachedData));
          setLoading(false);
          return;
        }
      }
    }

    // Se é refresh, usa estado diferente para não mostrar tela de loading
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await getClientes(userId);
      if (result.success && result.data) {
        setClientes(result.data);

        // Salva no cache
        sessionStorage.setItem(cacheKey, JSON.stringify(result.data));
        sessionStorage.setItem(cacheTimeKey, Date.now().toString());
      } else {
        setError(result.error || "Erro ao carregar clientes");
      }
    } catch (err) {
      console.error("Erro ao carregar clientes:", err);
      setError("Erro ao carregar clientes");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadClientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDelete = async (id: string) => {
    const cliente = clientes.find((c) => c.id === id);
    showConfirm(
      "Excluir Cliente",
      `Tem certeza que deseja excluir o cliente ${cliente?.nome || "não identificado"}? Esta ação não pode ser desfeita.`,
      async () => {
        const res = await deleteClienteByUser(id);
        if (res.success) {
          await loadClientes(true); // força refresh do Firestore
          setShowForm(false); // Fechar modal de edição após exclusão
          setEditingCliente(null);
        } else alert(res.error || "Erro ao excluir cliente");
      },
      "danger",
      "Sim, Excluir",
      "Cancelar",
    );
  };

  const handleEdit = (cliente: Cliente) => {
    setEditingCliente(cliente);
    setShowForm(true);
  };

  const handleSave = async (clienteData: ClienteFormData) => {
    try {
      const res = editingCliente
        ? await salvarCliente(clienteData, editingCliente.id)
        : await salvarCliente(clienteData);

      if (res.success) {
        await loadClientes(true); // força refresh do Firestore
      } else throw new Error(res.error || "Erro ao salvar cliente");
    } catch (e: any) {
      throw e;
    }
  };

  // Configuração das colunas do DataList
  const columns: DataListColumn<Cliente>[] = [
    {
      label: "Cliente",
      name: "nome",
      size: 5,
    },
    {
      label: "Telefone",
      name: "telefone",
      size: 3,
    },
    {
      label: "CPF",
      name: "cpf",
      size: 3,
    },
    {
      label: "",
      size: 1,
      render: (cliente) => (
        <div>
          <Button
            btnColor="warning"
            extraCss="text-white"
             onClick={(e) => {
              e.stopPropagation();
              handleEdit(cliente);
            }}
           ><FaEye size={16} /></Button>
          {/* <Button
            btnColor="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(cliente);
            }}
            style={{ borderRadius: "8px", padding: "8px" }}
          >
            <Edit size={16} />
          </Button>
          <Button
            btnColor="danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(cliente.id!);
            }}
            style={{ borderRadius: "8px", padding: "8px" }}
          >
            <Trash2 size={16} />
          </Button> */}
        </div>
      ),
    },
  ];

  // Filtro de busca
  const filteredClientes = clientes.filter(
    (cliente) =>
      cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.telefone.includes(searchTerm) ||
      cliente.cpf.includes(searchTerm) ||
      (cliente.endereco?.cidade &&
        cliente.endereco.cidade
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (cliente.endereco?.bairro &&
        cliente.endereco.bairro
          .toLowerCase()
          .includes(searchTerm.toLowerCase())),
  );

  // Paginação client-side sobre os dados filtrados
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClientes = filteredClientes.slice(startIndex, endIndex);

  // Reset página ao buscar ou mudar itens por página
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  if (loading || !userId) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="clientes-list-page">
      {/* Header com estatísticas */}
      <MainHeader
        title="Gestão de Clientes"
        subtitle={`Total de ${clientes.length} cliente${clientes.length !== 1 ? "s" : ""} cadastrado${clientes.length !== 1 ? "s" : ""}`}
        icon={<Users size={32} />}
      >
        {/* <Button
          btnColor="secondary"
          onClick={() => loadClientes(true)}
          disabled={refreshing}
          className="modern-btn d-flex align-items-center gap-2"
          style={{ borderRadius: "12px", padding: "12px 24px" }}
          title="Atualizar lista do servidor"
        >
          <RefreshCw
            size={20}
            className={refreshing ? "spinning" : ""}
          />
          {refreshing ? "Atualizando..." : "Atualizar"}
        </Button> */}

        <Button
          btnColor="success"
          onClick={() => {
            setEditingCliente(null);
            setShowForm(true);
          }}
          className="modern-btn d-flex align-items-center gap-2"
          style={{ borderRadius: "12px", padding: "12px 24px" }}
        >
          <Plus size={20} />
          Novo Cliente
        </Button>
      </MainHeader>

      {/* Card de busca e filtros */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "16px" }}>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col xs={12} md={8}>
                  <div className="position-relative">
                    <Search
                      size={20}
                      className="position-absolute"
                      style={{ top: "12px", left: "12px", color: "#6c757d" }}
                    />
                    <Form.Control
                      type="text"
                      placeholder="Buscar clientes por nome, telefone ou CPF..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="ps-5"
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e9ecef",
                        padding: "12px 16px",
                      }}
                    />
                  </div>
                </Col>
                {/* <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                  <Badge
                    bg="info"
                    className="px-3 py-2 d-inline-block"
                    style={{ borderRadius: "8px", fontSize: "14px" }}
                  >
                    // {filteredClientes.length} resultado
                    // {filteredClientes.length !== 1 ? "s" : ""}
                  </Badge>
                </Col> */}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de clientes */}
      <Row>
        <Col>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <DataList
            columns={columns}
            data={paginatedClientes}
            rowKey={(cliente) => cliente.id!}
            disableHoverEffects={true}
            emptyComponent={
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "16px" }}
              >
                <Card.Body className="text-center text-muted py-5">
                  <div className="d-flex flex-column align-items-center gap-3">
                    <Users size={48} className="text-muted" />
                    <div>
                      <h5 className="text-muted mb-2">
                        {searchTerm
                          ? "Nenhum cliente encontrado"
                          : "Nenhum cliente cadastrado"}
                      </h5>
                      <p className="text-muted mb-0">
                        {searchTerm
                          ? "Tente ajustar os termos de busca"
                          : "Comece adicionando seu primeiro cliente"}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            }
          />
          {filteredClientes.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredClientes.length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              onItemsPerPageChange={setItemsPerPage}
              showItemsPerPage={true}
              itemsPerPageOptions={[5, 10, 20, 50]}
              className="mt-3"
            />
          )}
        </Col>
      </Row>

      <ClienteForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingCliente(null);
        }}
        onSave={handleSave}
        onDelete={async (id: string) => {
          await handleDelete(id);
        }}
        cliente={editingCliente}
      />

      <ConfirmModal
        show={modalState.show}
        onHide={hideConfirm}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        loading={modalState.loading}
      />
    </Container>
  );
};

export default ClientesList;
